import React, { useState, useEffect, useCallback } from 'react';
import api, { errMsg } from '../api';
import { formatUGX } from '../mock';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { Loader2, Wallet, TrendingDown } from 'lucide-react';

const MOVE_TONE = {
  SALE: 'bg-red-50 text-red-700 border-red-200',
  RESTOCK: 'bg-green-50 text-green-700 border-green-200',
  RETURN: 'bg-blue-50 text-blue-700 border-blue-200',
  ADJUSTMENT: 'bg-amber-50 text-amber-700 border-amber-200',
  EXPIRY_WRITE_OFF: 'bg-purple-50 text-purple-700 border-purple-200',
  DAMAGE_WRITE_OFF: 'bg-slate-100 text-slate-700 border-slate-200',
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

const Section = ({ children }) => <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto mt-4">{children}</div>;
const Loading = () => <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="animate-spin mr-2" /> Loading…</div>;

const ReportsPage = () => {
  const { toast } = useToast();
  const [tab, setTab] = useState('valuation');
  const [loading, setLoading] = useState(false);
  const [valuation, setValuation] = useState(null);
  const [movements, setMovements] = useState([]);
  const [expiry, setExpiry] = useState([]);
  const [stockouts, setStockouts] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [products, setProducts] = useState([]);
  const [filters, setFilters] = useState({ product_id: 'all', movement_type: 'all', date_from: '', date_to: '' });
  const [expiryDays, setExpiryDays] = useState('90');

  useEffect(() => { api.get('/products').then((r) => setProducts(r.data)).catch(() => {}); }, []);

  const loadTab = useCallback(async (t) => {
    setLoading(true);
    try {
      if (t === 'valuation') setValuation((await api.get('/reports/valuation')).data);
      else if (t === 'expiry') setExpiry((await api.get('/reports/expiry', { params: { days: Number(expiryDays) || 90 } })).data);
      else if (t === 'stockouts') setStockouts((await api.get('/reports/stockouts')).data);
      else if (t === 'adjustments') setAdjustments((await api.get('/reports/adjustments')).data);
      else if (t === 'movements') {
        const params = {};
        if (filters.product_id !== 'all') params.product_id = filters.product_id;
        if (filters.movement_type !== 'all') params.movement_type = filters.movement_type;
        if (filters.date_from) params.date_from = filters.date_from;
        if (filters.date_to) params.date_to = filters.date_to;
        setMovements((await api.get('/reports/movements', { params })).data);
      }
    } catch (e) { toast({ title: 'Could not load report', description: errMsg(e), variant: 'destructive' }); }
    finally { setLoading(false); }
  }, [toast, filters, expiryDays]);

  useEffect(() => { loadTab(tab); /* eslint-disable-next-line */ }, [tab]);

  return (
    <div>
      <h1 className="font-display font-extrabold text-4xl text-slate-900">Reports</h1>
      <p className="text-slate-500 mt-2 text-[15px]">Stock valuation, movement history, expiry, stock-outs and the adjustment log.</p>

      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList className="bg-transparent p-0 h-auto gap-6 border-b border-slate-200 rounded-none w-full justify-start flex-wrap">
          {[
            { v: 'valuation', l: 'Valuation' },
            { v: 'movements', l: 'Stock movements' },
            { v: 'expiry', l: 'Expiry' },
            { v: 'stockouts', l: 'Stock-outs' },
            { v: 'adjustments', l: 'Adjustment log' },
          ].map((t) => (
            <TabsTrigger key={t.v} value={t.v}
              className="px-0 pb-3 rounded-none bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-blue-600 border-b-2 border-transparent data-[state=active]:border-blue-600 text-slate-500 font-semibold text-[15px]">
              {t.l}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Valuation */}
        <TabsContent value="valuation">
          {loading ? <Loading /> : valuation && (
            <>
              <div className="bg-white rounded-2xl border border-slate-200 p-5 flex items-center gap-4 mt-4 max-w-sm">
                <div className="h-12 w-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center"><Wallet size={22} /></div>
                <div><div className="font-display font-extrabold text-2xl text-slate-900">{formatUGX(valuation.total)}</div><div className="text-slate-500 text-sm">Total stock value at cost</div></div>
              </div>
              <Section>
                <table className="w-full text-[15px] min-w-[560px]">
                  <thead><tr className="text-left text-slate-500 text-sm border-b border-slate-100">
                    <th className="px-6 py-3 font-semibold">Product</th><th className="px-6 py-3 font-semibold">On hand</th><th className="px-6 py-3 font-semibold text-right">Cost value</th>
                  </tr></thead>
                  <tbody className="divide-y divide-slate-100">
                    {valuation.rows.map((r) => (
                      <tr key={r.product_id} className="hover:bg-slate-50"><td className="px-6 py-4 font-medium text-slate-800">{r.name}</td><td className="px-6 py-4 text-slate-600">{r.on_hand}</td><td className="px-6 py-4 text-right font-semibold text-slate-800">{formatUGX(r.cost_value)}</td></tr>
                    ))}
                    {valuation.rows.length === 0 && <tr><td colSpan={3} className="px-6 py-10 text-center text-slate-400">No stock on hand yet.</td></tr>}
                  </tbody>
                </table>
              </Section>
            </>
          )}
        </TabsContent>

        {/* Movements */}
        <TabsContent value="movements">
          <div className="flex flex-wrap gap-3 mt-4 items-end">
            <div className="w-56">
              <Label className="text-slate-600 text-sm">Product</Label>
              <Select value={filters.product_id} onValueChange={(v) => setFilters({ ...filters, product_id: v })}>
                <SelectTrigger className="mt-1.5 h-10 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All products</SelectItem>{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="w-48">
              <Label className="text-slate-600 text-sm">Type</Label>
              <Select value={filters.movement_type} onValueChange={(v) => setFilters({ ...filters, movement_type: v })}>
                <SelectTrigger className="mt-1.5 h-10 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  {['SALE', 'RESTOCK', 'RETURN', 'ADJUSTMENT', 'EXPIRY_WRITE_OFF', 'DAMAGE_WRITE_OFF'].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-slate-600 text-sm">From</Label><Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="mt-1.5 h-10 bg-white w-40" /></div>
            <div><Label className="text-slate-600 text-sm">To</Label><Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="mt-1.5 h-10 bg-white w-40" /></div>
            <button onClick={() => loadTab('movements')} className="h-10 px-4 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold">Apply</button>
          </div>
          {loading ? <Loading /> : (
            <Section>
              <table className="w-full text-[15px] min-w-[820px]">
                <thead><tr className="text-left text-slate-500 text-sm border-b border-slate-100">
                  <th className="px-6 py-3 font-semibold">When</th><th className="px-6 py-3 font-semibold">Product</th><th className="px-6 py-3 font-semibold">Type</th><th className="px-6 py-3 font-semibold">Batch</th><th className="px-6 py-3 font-semibold">Qty</th><th className="px-6 py-3 font-semibold text-right">Running balance</th>
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {movements.map((m) => (
                    <tr key={m.id} className="hover:bg-slate-50">
                      <td className="px-6 py-4 text-slate-600">{fmtDate(m.created_at)}</td>
                      <td className="px-6 py-4 font-medium text-slate-800">{m.product_name}</td>
                      <td className="px-6 py-4"><Badge variant="outline" className={MOVE_TONE[m.movement_type]}>{m.movement_type}</Badge></td>
                      <td className="px-6 py-4 text-slate-600">{m.batch_no}</td>
                      <td className={`px-6 py-4 font-semibold ${m.quantity < 0 ? 'text-red-600' : 'text-green-600'}`}>{m.quantity > 0 ? '+' : ''}{m.quantity}</td>
                      <td className="px-6 py-4 text-right text-slate-700">{m.running_balance}</td>
                    </tr>
                  ))}
                  {movements.length === 0 && <tr><td colSpan={6} className="px-6 py-10 text-center text-slate-400">No movements found.</td></tr>}
                </tbody>
              </table>
            </Section>
          )}
        </TabsContent>

        {/* Expiry */}
        <TabsContent value="expiry">
          <div className="flex items-end gap-3 mt-4">
            <div><Label className="text-slate-600 text-sm">Within (days)</Label><Input type="number" value={expiryDays} onChange={(e) => setExpiryDays(e.target.value)} className="mt-1.5 h-10 bg-white w-32" /></div>
            <button onClick={() => loadTab('expiry')} className="h-10 px-4 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold">Apply</button>
          </div>
          {loading ? <Loading /> : (
            <Section>
              <table className="w-full text-[15px] min-w-[560px]">
                <thead><tr className="text-left text-slate-500 text-sm border-b border-slate-100"><th className="px-6 py-3 font-semibold">Product</th><th className="px-6 py-3 font-semibold">Batch</th><th className="px-6 py-3 font-semibold">Expiry</th><th className="px-6 py-3 font-semibold">On hand</th><th className="px-6 py-3 font-semibold">Status</th></tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {expiry.map((r, i) => (
                    <tr key={i} className="hover:bg-slate-50"><td className="px-6 py-4 font-medium text-slate-800">{r.product_name}</td><td className="px-6 py-4 text-slate-600">{r.batch_no}</td><td className="px-6 py-4 text-slate-600">{r.expiry_date}</td><td className="px-6 py-4 text-slate-600">{r.on_hand}</td><td className="px-6 py-4">{r.expired ? <Badge className="bg-red-100 text-red-700">Expired</Badge> : <Badge className="bg-amber-100 text-amber-700">Expiring soon</Badge>}</td></tr>
                  ))}
                  {expiry.length === 0 && <tr><td colSpan={5} className="px-6 py-10 text-center text-slate-400">Nothing expiring in this window.</td></tr>}
                </tbody>
              </table>
            </Section>
          )}
        </TabsContent>

        {/* Stock-outs */}
        <TabsContent value="stockouts">
          {loading ? <Loading /> : (
            <Section>
              <table className="w-full text-[15px] min-w-[460px]">
                <thead><tr className="text-left text-slate-500 text-sm border-b border-slate-100"><th className="px-6 py-3 font-semibold">Product</th><th className="px-6 py-3 font-semibold">Out since</th></tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {stockouts.map((r) => (<tr key={r.product_id} className="hover:bg-slate-50"><td className="px-6 py-4 font-medium text-slate-800">{r.name}</td><td className="px-6 py-4 text-slate-600">{r.since ? fmtDate(r.since) : 'Never stocked'}</td></tr>))}
                  {stockouts.length === 0 && <tr><td colSpan={2} className="px-6 py-10 text-center text-slate-400">No products are out of stock.</td></tr>}
                </tbody>
              </table>
            </Section>
          )}
        </TabsContent>

        {/* Adjustment log */}
        <TabsContent value="adjustments">
          {loading ? <Loading /> : (
            <Section>
              <table className="w-full text-[15px] min-w-[820px]">
                <thead><tr className="text-left text-slate-500 text-sm border-b border-slate-100"><th className="px-6 py-3 font-semibold">When</th><th className="px-6 py-3 font-semibold">Product</th><th className="px-6 py-3 font-semibold">Type</th><th className="px-6 py-3 font-semibold">Qty</th><th className="px-6 py-3 font-semibold">User</th><th className="px-6 py-3 font-semibold">Reason</th></tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {adjustments.map((m) => (
                    <tr key={m.id} className="hover:bg-slate-50"><td className="px-6 py-4 text-slate-600">{fmtDate(m.created_at)}</td><td className="px-6 py-4 font-medium text-slate-800">{m.product_name}</td><td className="px-6 py-4"><Badge variant="outline" className={MOVE_TONE[m.movement_type]}>{m.movement_type}</Badge></td><td className={`px-6 py-4 font-semibold ${m.quantity < 0 ? 'text-red-600' : 'text-green-600'}`}>{m.quantity > 0 ? '+' : ''}{m.quantity}</td><td className="px-6 py-4 text-slate-600">{m.user_name}</td><td className="px-6 py-4 text-slate-600">{m.reason}</td></tr>
                  ))}
                  {adjustments.length === 0 && <tr><td colSpan={6} className="px-6 py-10 text-center text-slate-400">No adjustments recorded.</td></tr>}
                </tbody>
              </table>
            </Section>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReportsPage;
